import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper, Container } from '@mui/material';
import { Refresh, ShieldOutlined } from '@mui/icons-material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error in application:', error, errorInfo);
    
    // If it is a chunk load error (due to dev server restart or new deployment), automatically reload once
    if (error.name === 'ChunkLoadError' || error.message.includes('Loading chunk')) {
      const lastReload = sessionStorage.getItem('chunk_load_last_reload');
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        sessionStorage.setItem('chunk_load_last_reload', now.toString());
        window.location.reload();
      }
    }
  }

  private handleReload = () => {
    sessionStorage.removeItem('chunk_load_last_reload');
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#0B2847',
            p: 3
          }}
        >
          <Container maxWidth="sm">
            <Paper
              elevation={10}
              sx={{
                p: { xs: 4, sm: 5 },
                borderRadius: 4,
                textAlign: 'center',
                bgcolor: '#FFFFFF'
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: 'rgba(197, 155, 39, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <ShieldOutlined sx={{ fontSize: 36, color: '#C59B27' }} />
              </Box>

              <Typography variant="h5" fontWeight={800} color="#0B2847" gutterBottom>
                Workspace Synchronizing
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5 }}>
                A recent update was deployed to the portal. Please refresh your browser to load the latest secure workspace version.
              </Typography>

              <Button
                variant="contained"
                size="large"
                startIcon={<Refresh />}
                onClick={this.handleReload}
                sx={{
                  bgcolor: '#123D6B',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 4,
                  py: 1.25,
                  borderRadius: 2.5,
                  boxShadow: '0 4px 14px rgba(18, 61, 107, 0.3)',
                  '&:hover': { bgcolor: '#0B2847' }
                }}
              >
                Reload Workspace
              </Button>
            </Paper>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
